// main.js
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.min.css'
import '@fortawesome/fontawesome-free/css/all.css'
import 'material-design-icons-iconfont/dist/material-design-icons.css' 
import '@fortawesome/fontawesome-free/js/all.js'
import '@mdi/font/css/materialdesignicons.css'

import { createApp, watch } from 'vue';
import { createPinia, setActivePinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import App from './App.vue';
import router from './router'
import { createVuetify } from 'vuetify';
import { fa } from 'vuetify/iconsets/fa'
import { mdi } from 'vuetify/iconsets/mdi'
import { md } from 'vuetify/iconsets/md'
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import apiClientPlugin from './plugins/apiClient';
import RPilotChatPlugin from '@rpilot/chat/plugin.js';
import { useSettingsStore } from './stores/settings';
import { messages } from './locales';
import { startLongTaskLogger } from './utils/perf';

if (import.meta.env.DEV) {
  startLongTaskLogger();
}


const pinia = createPinia();
setActivePinia(pinia);
const settingsStore = useSettingsStore();

export const i18n = createI18n({
  locale: settingsStore.currentLocale,
  fallbackLocale: 'defaultLocale',
  messages,
});

watch(
  () => settingsStore.settings.locale,
  (newLocale) => {
    i18n.global.locale = newLocale;
  }
);


const dark = {
  dark: true,
  colors: {
    // Matches the host RDrive dashboard shell: neutral charcoal/slate greys
    // (header bar, pill container, active pill) rather than the old blue tint.
    background: '#263238',
    surface: '#37424A',
    'surface-bright': '#485258',
    'surface-light': '#3F4A51',
    'surface-variant': '#485258',
    'on-background': '#E9EDEF',
    'on-surface': '#E9EDEF',
    'on-surface-variant': '#E9EDEF',
    primary: '#AAB2B6',
    'primary-darken-1': '#929DA4',
    secondary: '#485258',
    'secondary-darken-1': '#1C262B',
    error: '#FF5252',
    info: '#2196F3',
    success: '#4CAF50',
    warning: '#FB8C00',
  },
  variables: {
    // Vuetify's dark defaults (0.70 / 1) still read washed out against the
    // mid-tone slate surfaces, so lift secondary text closer to white.
    'medium-emphasis-opacity': 0.85,
    'high-emphasis-opacity': 1,
  },
};

const light = {
  dark: false,
  colors: {
    background: '#F5F5F5',
    surface: '#FFFFFF',
    'surface-variant': '#EEF2F6',
    'on-surface-variant': '#1E2A33',
    primary: '#1976D2',
    'primary-darken-1': '#1565C0',
    secondary: '#424242',
    'secondary-darken-1': '#212121',
    error: '#D32F2F',
    info: '#2196F3',
    success: '#4CAF50',
    warning: '#FFA000',
  },
};

// ** warning - custom visual doesn't use this, it uses vuetify.js
const vuetify = createVuetify({
  theme: {
    defaultTheme: 'light',
    themes: {
      dark,
      light,
    },
  },
  components,
  directives,
  icons: {
    defaultSet: 'mdi',
    sets: {
      mdi,
      md,
      fa 
    },
  },
});

/*window.addEventListener('message', function(event) {
  if (event.origin !== 'https://project-overview.dashboards.rdrive.io') return;
  
  switch (event.data.action) {
    case 'showPlanViewer':
      break;
    case 'showTaskViewer':
      break;
    case 'showForm':
      break;
    case 'showIssue':
      break;
    case 'downloadIssues':
      break;
    case 'downloadProcesses':
      snagr.download.downloadProcesses(event.data.ids, event.data.options);
      break;
    case 'downloadForms':
      // Add logic for downloadForms
      break;
    default:
      console.log('Unknown action:', event.data.action);
  }
}, false);*/

const app = createApp(App);
app.use(pinia);
app.use(router);
app.use(vuetify);
app.use(apiClientPlugin);
app.use(i18n);
// Auth (scopedToken/apiUrl/projectCode) is bound on the <RPilotChat> instance
// itself in RPilotPage.vue — at app-install time those values are still null
// (parseQuerystring() in App.vue.onMounted hasn't run yet), and only the
// component-level prop watcher reacts when the Pinia state lands.
app.use(RPilotChatPlugin, {
  apiBaseUrl: (import.meta.env.VITE_RPILOT_MCP_URL ?? 'http://localhost:3001') + '/api',
  sessionCreateOptions: { agentCode: 'rdrive-task-manager' },
  namespace: 'rdrive-task-manager',
});
app.mount('#app');